/**
 * Single-tab enforcement: กันหลายแท็บ — ให้ใช้ได้แค่หนึ่งแท็บเมื่อล็อกอินอยู่
 * ใช้ localStorage + storage event: แท็บที่เปิดทีหลังจะเห็นว่ามี "owner" แท็บอยู่แล้ว และแสดง overlay
 * ป้องกันการเล่น/ถอนเงินจากหลายแท็บพร้อมกัน
 *
 * สำคัญ: ไม่ใช้ sessionStorage สำหรับ tabId เพราะเวลา duplicate tab ใน Chrome
 * sessionStorage จะถูก copy ไปด้วย ทำให้ทั้งสองแท็บได้ tabId เดียวกัน จึงใช้ useRef
 * สร้าง id ครั้งเดียวต่อ document แท็บที่ duplicate จะเป็น document ใหม่ จึงได้ id ใหม่
 */
import { useEffect, useRef, useState, useCallback } from 'react'

const STORAGE_KEY = 'huay_single_tab_owner'
const HEARTBEAT_INTERVAL_MS = 2000
const STALE_THRESHOLD_MS = 10000
const RECHECK_DUPLICATE_MS = 5000

interface OwnerData {
  tabId: string
  updatedAt: number
}

function createTabId(): string {
  return `tab_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`
}

/** หนึ่ง id ต่อ document (ต่อการโหลดหน้า) — duplicate tab = document ใหม่ = id ใหม่ */
let documentTabId: string | null = null
function getDocumentTabId(): string {
  if (!documentTabId) documentTabId = createTabId()
  return documentTabId
}

function readOwner(): OwnerData | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const data = JSON.parse(raw) as OwnerData
    return data.tabId && typeof data.updatedAt === 'number' ? data : null
  } catch {
    return null
  }
}

function writeOwner(tabId: string): void {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ tabId, updatedAt: Date.now() } as OwnerData)
    )
  } catch (e) {
    console.warn('[useSingleTabEnforcement] writeOwner failed', e)
  }
}

function clearOwner(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {}
}

export function useSingleTabEnforcement(enabled: boolean) {
  const [isDuplicateTab, setIsDuplicateTab] = useState(false)
  const tabIdRef = useRef(getDocumentTabId())
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const recheckRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const takeOverSession = useCallback(() => {
    writeOwner(tabIdRef.current)
    setIsDuplicateTab(false)
  }, [])

  useEffect(() => {
    if (!enabled) {
      clearOwner()
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current)
        heartbeatRef.current = null
      }
      if (recheckRef.current) {
        clearInterval(recheckRef.current)
        recheckRef.current = null
      }
      setIsDuplicateTab(false)
      return
    }

    const myId = tabIdRef.current

    // เมื่อเปิดหน้านี้ (หรือเพิ่ง login) ให้ถือว่าแท็บนี้เป็นเจ้าของก่อนเสมอ
    // ถ้ามีแท็บอื่นเปิดอยู่ แท็บนั้นจะได้ storage event จาก write นี้แล้วไปแสดง overlay เอง
    // ป้องกันกรณี localStorage เหลือจาก session เก่า (แท็บปิดไปแล้ว) ที่ทำให้ขึ้น "มีการใช้งานจากอีกแท็บ" ผิด
    writeOwner(myId)
    setIsDuplicateTab(false)

    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY || e.newValue === null) return
      try {
        const data = JSON.parse(e.newValue) as OwnerData
        if (data.tabId !== myId && Date.now() - data.updatedAt < STALE_THRESHOLD_MS) {
          setIsDuplicateTab(true)
        }
      } catch {}
    }

    window.addEventListener('storage', onStorage)

    heartbeatRef.current = setInterval(() => {
      const current = readOwner()
      if (current?.tabId === myId) writeOwner(myId)
    }, HEARTBEAT_INTERVAL_MS)

    recheckRef.current = setInterval(() => {
      const owner = readOwner()
      if (!owner || owner.tabId === myId) return
      if (Date.now() - owner.updatedAt > STALE_THRESHOLD_MS) {
        writeOwner(myId)
        setIsDuplicateTab(false)
      }
    }, RECHECK_DUPLICATE_MS)

    return () => {
      window.removeEventListener('storage', onStorage)
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current)
        heartbeatRef.current = null
      }
      if (recheckRef.current) {
        clearInterval(recheckRef.current)
        recheckRef.current = null
      }
    }
  }, [enabled])

  return { isDuplicateTab, takeOverSession }
}
